/**
 * useStableSelectHandler.ts
 */
import { useCallback, useRef } from 'react';

/**
 * Returns a getter for stable, per-index `onClick` handlers so that
 * `React.memo` on list items (Cards) isn't invalidated every render by a
 * freshly created closure. Handlers are cached per index and the cache is
 * reset whenever `id` changes (e.g. the rail's data was replaced).
 */
export default function useStableSelectHandler(
  id: string,
  select: (id: string, index: number) => void
) {
  const cacheRef = useRef<{ id: string; handlers: Map<number, () => void> }>({
    id,
    handlers: new Map(),
  });

  if (cacheRef.current.id !== id) {
    cacheRef.current = { id, handlers: new Map() };
  }

  return useCallback(
    (index: number) => {
      const { handlers } = cacheRef.current;
      let handler = handlers.get(index);
      if (!handler) {
        handler = () => select(id, index);
        handlers.set(index, handler);
      }
      return handler;
    },
    [id, select]
  );
}
